<script setup lang="ts">import { ref as _ref, computed as _computed, toRef as _toRef } from 'vue';


import {
  IFileVariantResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IFileVariantResponseInstance'
import {useBestImage} from '@toolify/client/src/modules/ui/composables/useBestImage'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import {watch} from 'vue'
import {ImageLoadState} from '@toolify/client/src/modules/ui/enum/ImageLoadState'

interface Props {
  src?: string
  alt: string
  fileVariants?: IFileVariantResponseInstance[]
  errorIconNameType?: IconNameType
  /** Keep skeleton visible while a real src is still being resolved externally. */
  isAwaitingSrc?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  errorIconNameType: IconNameType.File,
  isAwaitingSrc: false,
})

const elementRef = _ref<HTMLElement>(null)
const imageOptions = _computed(() => ({
  fileVariants: props.fileVariants?.map(e => e),
  url: props.src,
}))
const __$temp_1 = (useBestImage((elementRef), (imageOptions))),
  bestFileVariantUrl = _toRef(__$temp_1, 'bestFileVariantUrl'),
  resolvedLoadState = _toRef(__$temp_1, 'loadState');

const loadState = _computed(() => {
  if(props.isAwaitingSrc && !bestFileVariantUrl.value) {
    return ImageLoadState.Loading
  }
  return resolvedLoadState.value
})

const delay = Math.random() * .5 - 1

const imageLoadingStyle = {
  animationDelay: `${delay}s`,
}
// let hasBeenDebounced = $ref(false)
// onMounted(() => {
//   setTimeout(() => {
//     hasBeenDebounced = true
//   }, 25)
// })
// const isLoading = $computed<boolean>(() => {
//   return !hasBeenDebounced || isImageLoading
// })

let hasDebounced = _ref(false)
let debounceTimeout = null
watch(() => props.fileVariants, () => {
  if(hasDebounced.value) {
    return
  }
  if(debounceTimeout) {
    clearTimeout(debounceTimeout)
  }
  debounceTimeout = setTimeout(() => {
    hasDebounced.value = true
  }, 500)
}, {
  immediate: true,
})

</script>

<template>
  <div class="image" :class="{'is-fading-in': !hasDebounced}" ref="elementRef">
    <img :src="bestFileVariantUrl" :alt="props.alt" v-if="loadState === ImageLoadState.Loaded"/>
    <slot name="error" v-else-if="loadState === ImageLoadState.Failed">
      <IconComponent
        :name="errorIconNameType"
        class="error-icon"
        :weightType="IconWeightType.Solid"
      />
    </slot>
    <div
      v-else
      class="image-loading"
    >
      <div class="image-loading-animation" :style="imageLoadingStyle"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @use '../../../styles/variables' as variables;

  @keyframes blink-loading-animation {
    0%, 100% {
      opacity: .25;
    }

    50% {
      opacity:1
    }
  }

  // .image, img, .image-loading { //TODO: remove if not needed
  //   border-radius: calc(100% * 1/3);
  // }
  img {
    object-fit: cover;
  }

  img, .image-loading {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    //z-index: 1;
  }

  .image {
    width: 100%;
    height: 100%;
    position: relative;
    background: map-get(variables.$colors, container);
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .error {

  }
  .error-icon {
    color: map-get(variables.$colors, content-dim);
    width: 50% !important;
    height: 50% !important;
    :deep(.icon-content) {
      width: 100%;
      height: 100%;
    }
  }

  .image-loading {

  }

  .image-loading-animation {
    width: 100%;
    height: 100%;
    animation: blink-loading-animation 1.5s linear infinite;
    background: map-get(variables.$colors, container-high);
  }

  img {
    width: 100%;
    height: 100%;
  }

  @keyframes image-fade-in {
    from {
      opacity: 0;
    }
  }

  // A CSS animation instead of <Transition>: the transition forces a layout on every swap, once per image.
  .image.is-fading-in > :deep(:not(.image-loading)) {
    animation: image-fade-in .1s ease;
  }
</style>
